/**
 * Visual-only mention detection. Mentions are stored as plain `@Name` text (the backend parses
 * them that way), so highlighting is derived at render time from the list of known names.
 */

export type MentionKind = 'employee' | 'ai';

export type MentionTerm = {
  name: string;
  kind: MentionKind;
};

export type MentionRange = {
  start: number;
  end: number;
  kind: MentionKind;
  name: string;
};

const escapeRegex = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/** Characters allowed right after a mention so `@Name,` and `@Name.` still match. */
const TRAILING_BOUNDARY = '(?=$|[\\s.,!?;:)\\]])';

/** Characters allowed right before `@` so `(@Name)` and line starts match but `a@b` does not. */
const LEADING_BOUNDARY = '(^|[\\s(\\[])';

const buildMentionRegex = (terms: readonly MentionTerm[]): RegExp | null => {
  const names = terms
    .map(term => term.name.trim())
    .filter(Boolean)
    // Longest first so "Ali Hasanov" wins over "Ali" when both exist.
    .sort((left, right) => right.length - left.length);
  if (names.length === 0) {
    return null;
  }

  const alternation = Array.from(new Set(names.map(name => name.toLowerCase())))
    .map(escapeRegex)
    .join('|');
  return new RegExp(`${LEADING_BOUNDARY}@(${alternation})${TRAILING_BOUNDARY}`, 'gi');
};

/** Finds `@Name` occurrences for the known terms inside a plain-text string. */
export const findMentionRanges = (text: string, terms: readonly MentionTerm[]): MentionRange[] => {
  if (!text || terms.length === 0) {
    return [];
  }

  const regex = buildMentionRegex(terms);
  if (!regex) {
    return [];
  }

  const kindByName = new Map<string, MentionTerm>();
  for (const term of terms) {
    const key = term.name.trim().toLowerCase();
    // AI wins on collision so a teammate literally named like the agent still reads as AI.
    if (key && (!kindByName.has(key) || term.kind === 'ai')) {
      kindByName.set(key, term);
    }
  }

  const ranges: MentionRange[] = [];
  let match: RegExpExecArray | null;
  while ((match = regex.exec(text)) !== null) {
    if (match[0].length === 0) {
      regex.lastIndex += 1;
      continue;
    }

    const leading = match[1] ?? '';
    const matchedName = match[2] ?? '';
    const term = kindByName.get(matchedName.toLowerCase());
    if (!term) {
      continue;
    }

    const start = match.index + leading.length;
    ranges.push({
      start,
      end: start + matchedName.length + 1,
      kind: term.kind,
      name: term.name,
    });
  }

  return ranges;
};

export type MentionChipClassNames = {
  employee: string;
  ai: string;
};

/**
 * Wraps `@Name` text inside already-sanitized HTML with chip spans. Runs after sanitization and
 * only inserts elements it builds itself, so no untrusted markup can slip through.
 */
export const decorateMentionsHtml = (
  sanitizedHtml: string,
  terms: readonly MentionTerm[],
  classNames: MentionChipClassNames,
): string => {
  if (!sanitizedHtml || terms.length === 0 || typeof document === 'undefined') {
    return sanitizedHtml;
  }

  const container = document.createElement('div');
  container.innerHTML = sanitizedHtml;

  const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT);
  const textNodes: Text[] = [];
  for (let node = walker.nextNode(); node; node = walker.nextNode()) {
    textNodes.push(node as Text);
  }

  let changed = false;
  for (const textNode of textNodes) {
    const text = textNode.data;
    const ranges = findMentionRanges(text, terms);
    if (ranges.length === 0) {
      continue;
    }

    const fragment = document.createDocumentFragment();
    let cursor = 0;
    for (const range of ranges) {
      if (range.start > cursor) {
        fragment.appendChild(document.createTextNode(text.slice(cursor, range.start)));
      }

      const chip = document.createElement('span');
      chip.className = range.kind === 'ai' ? classNames.ai : classNames.employee;
      chip.setAttribute('data-mention-kind', range.kind);
      chip.textContent = text.slice(range.start, range.end);
      fragment.appendChild(chip);
      cursor = range.end;
    }

    if (cursor < text.length) {
      fragment.appendChild(document.createTextNode(text.slice(cursor)));
    }

    textNode.parentNode?.replaceChild(fragment, textNode);
    changed = true;
  }

  return changed ? container.innerHTML : sanitizedHtml;
};
